import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { getScreenshotPreview, getScreenshotPreviewSet } from '../lib/screenshotPreview';
import { selectedCaptureSrc, SelectedCaptureKey } from '../lib/selectedCaptures';
import UpdatesSignup from '../components/UpdatesSignup';
import Footer from '../components/Footer';
import AppStoreBadges from '../components/AppStoreBadges';
import DeferredBackground from '../components/DeferredBackground';
import DeferredScreenshot from '../components/DeferredScreenshot';

const photoHeading = '#f8fafc', photoBody = 'rgba(226,232,240,0.9)';

const ArrowIcon: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
);

const deviceFrame = 'rounded-[2rem] border-4 overflow-hidden shadow-2xl';

const dashboardViews: { capture: SelectedCaptureKey; label: string }[] = [
  { capture: 'dashboardMap', label: 'Map view' },
  { capture: 'dashboardList', label: 'List view' },
  { capture: 'dashboardHybrid', label: 'Hybrid view' },
  { capture: 'selectedEvent', label: 'Event card' }
];

const AnimatedSection: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1, rootMargin: '0px' });
  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return (
    <div
      ref={ref}
      style={
        reducedMotion
          ? undefined
          : {
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: `opacity 1.2s ease-out ${delay}ms, transform 1s ease-out ${delay}ms`
            }
      }
    >
      {children}
    </div>
  );
};

const Landing: React.FC = () => {
  const { colors, theme, dsColors } = useTheme();
  const [dashView, setDashView] = useState(0);

  const { textPrimary: heroPrimary, textSecondary: heroSecondary } = colors;
  const cardBorder = theme === 'light' ? 'rgba(0,0,0,0.1)' : colors.border;
  const deviceBorder = 'rgba(255,255,255,0.2)';
  const shot = (base: string) =>
    `/assets/screenshots/ios-delivery/${base} - ${theme === 'dark' ? 'Dark' : 'Light'}.png`;

  const cta =
    'inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-base sm:text-lg motion-safe:transition-all motion-safe:duration-300 motion-safe:hover:scale-105';

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Preload this route's hero so it wins against deferred below-fold media. */}
      <link
        rel="preload"
        as="image"
        href="/assets/images/marketing-concepts/ridepulse-sportbike-mountain-ride-text-left-v1.webp"
        {...{ fetchpriority: 'high' }}
      />
      {/* 1 · Hero — mountain sportbike; type in the designed left negative space */}
      <section className="relative bg-[#0a0d14] md:bg-transparent">
        <div className="md:hidden aspect-[16/10] bg-cover bg-[position:72%_center]" style={{ backgroundImage: `url('/assets/images/marketing-concepts/ridepulse-sportbike-mountain-ride-text-left-v1.webp')` }} role="img" aria-label="Sportbike leaning through a mountain road" />
        <div
          className="absolute inset-0 hidden md:block bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(100deg, rgba(8,10,16,0.7) 0%, rgba(8,10,16,0.5) 45%, rgba(8,10,16,0.14) 72%, transparent 88%), url('/assets/images/marketing-concepts/ridepulse-sportbike-mountain-ride-text-left-v1.webp')`
          }}
        />
        <div className="relative container mx-auto px-4 sm:px-6 max-w-6xl py-16 md:py-32 lg:py-40">
          <div className="max-w-2xl text-center md:text-left mx-auto md:mx-0">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.05]" style={{ color: photoHeading, textWrap: 'balance' }}>
              Find Riders. <br className="hidden md:block" />Roll Out.
            </h1>
            <p className="text-lg sm:text-xl leading-relaxed mb-8 max-w-md mx-auto md:mx-0" style={{ color: photoBody, textWrap: 'pretty' }}>
              RidePulse brings motorcyclists together through events: cruises, hangouts, tours, workshops. Find one near you, see who's coming and what they're riding, or host your own.
            </p>
            <div className="mb-10 flex justify-center md:justify-start">
              <AppStoreBadges size="large" showApple showAndroid />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 max-w-xl text-left mx-auto md:mx-0">
              {[
                ['Find Events Near You', "Browse what's happening on a map"],
                ['Host It Yourself', 'Familiar creation, motorcycle details'],
                ['Stay Connected', 'Chat and coordinate'],
                ['Privacy First', 'You control what others see']
              ].map(([title, sub]) => (
                <div key={title} className="flex items-start gap-3">
                  <span className="mt-2 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: dsColors.primary }} aria-hidden="true" />
                  <div>
                    <h3 className="font-semibold text-base mb-0.5" style={{ color: photoHeading }}>{title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: photoBody }}>{sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 2 · Origin story — quiet type band */}
      <AnimatedSection delay={100}>
        <section className="py-16 md:py-24" style={{ backgroundColor: colors.background }}>
          <div className="container mx-auto px-4 sm:px-6 max-w-3xl text-center">
            <p className="text-xs sm:text-sm font-bold uppercase tracking-widest mb-4" style={{ color: dsColors.primary }}>
              Built for Riders, By a Rider
            </p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-5 leading-tight max-w-2xl mx-auto" style={{ color: heroPrimary, textWrap: 'balance' }}>
              Built around the questions riders actually ask
            </h2>
            <p className="text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto mb-8" style={{ color: heroSecondary, textWrap: 'pretty' }}>
              At many of the rides I joined, the same questions came up: Who's leading? Where are we heading? Will my bike keep up? I built RidePulse so the answers live in the event itself: the host, the route, the riders and bikes coming, and the chat to sort the rest.
            </p>
            <Link
              to="/about"
              className={cta}
              style={{ color: heroPrimary, border: `2px solid ${dsColors.primary}`, minWidth: '44px', minHeight: '44px' }}
            >
              Read the whole story
              <ArrowIcon />
            </Link>
          </div>
        </section>
      </AnimatedSection>

      {/* 3 · Find events — device-led split */}
      <AnimatedSection delay={150}>
        <section className="py-16 md:py-24" style={{ backgroundColor: colors.surface }}>
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center">
              <div className="flex flex-col items-center gap-4">
                <DeferredScreenshot
                  src={getScreenshotPreview(selectedCaptureSrc(dashboardViews[dashView].capture, theme))}
                  srcSet={getScreenshotPreviewSet(selectedCaptureSrc(dashboardViews[dashView].capture, theme))}
                  sizes="(min-width: 768px) 288px, (min-width: 640px) 256px, 240px"
                  alt={`RidePulse dashboard, ${dashboardViews[dashView].label}`}
                  loading="lazy"
                  width={1320}
                  height={2868}
                  className={`${deviceFrame} w-60 sm:w-64 md:w-72 max-w-full`}
                  style={{ borderColor: deviceBorder }}
                />
                <div
                  role="group"
                  aria-label="Dashboard views"
                  className="flex flex-wrap justify-center gap-3"
                  onKeyDown={(e) => {
                    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
                    e.preventDefault();
                    const next = (dashView + (e.key === 'ArrowRight' ? 1 : dashboardViews.length - 1)) % dashboardViews.length;
                    setDashView(next);
                    ((e.currentTarget.querySelectorAll('button')[next]) as HTMLElement)?.focus();
                  }}
                >
                  {dashboardViews.map((v, i) => (
                    <button
                      key={v.label}
                      type="button"
                      aria-pressed={dashView === i}
                      aria-label={v.label}
                      onClick={() => setDashView(i)}
                      className="w-12 sm:w-14 rounded-lg overflow-hidden focus:outline-none focus-visible:ring-2 motion-safe:transition-opacity"
                      style={{
                        '--tw-ring-color': dsColors.primary,
                        border: `2px solid ${dashView === i ? dsColors.primary : cardBorder}`,
                        opacity: dashView === i ? 1 : 0.55
                      } as React.CSSProperties}
                    >
                      <DeferredScreenshot src={getScreenshotPreview(selectedCaptureSrc(v.capture, theme))} srcSet={getScreenshotPreviewSet(selectedCaptureSrc(v.capture, theme))} sizes="64px" alt="" aria-hidden="true" loading="lazy" width={1320} height={2868} className="w-full block pointer-events-none" />
                    </button>
                  ))}
                </div>
                <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: heroSecondary }}>
                  {dashboardViews[dashView].label}
                </p>
              </div>
              <div className="text-center md:text-left">
                <p className="text-xs sm:text-sm font-bold uppercase tracking-widest mb-3" style={{ color: dsColors.secondary }}>
                  Find Events
                </p>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 leading-tight" style={{ color: heroPrimary, textWrap: 'balance' }}>
                  Discover Events Near You
                </h2>
                <p className="text-lg sm:text-xl leading-relaxed mb-6" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  Browse motorcycle events on an interactive map and filter by type, distance, and date. See the details before you commit: who's hosting, who's coming, and what they're riding.
                </p>
                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                  {['Cruise', 'Hangout', 'Tour', 'Workshop'].map((type) => (
                    <span
                      key={type}
                      className="px-4 py-2 rounded-full text-sm font-semibold"
                      style={{
                        backgroundColor: theme === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                        color: heroPrimary,
                        border: `1px solid ${cardBorder}`
                      }}
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* 4 · Create/host — reverse split, one device */}
      <AnimatedSection delay={200}>
        <section className="py-16 md:py-24" style={{ backgroundColor: colors.background }}>
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center">
              <div className="text-center md:text-left">
                <p className="text-xs sm:text-sm font-bold uppercase tracking-widest mb-3" style={{ color: dsColors.primary }}>
                  Create Events
                </p>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 leading-tight" style={{ color: heroPrimary, textWrap: 'balance' }}>
                  Host Your Own Event
                </h2>
                <p className="text-lg sm:text-xl leading-relaxed mb-8" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  Creating an event should feel familiar. RidePulse brings the motorcycle-specific details into the plan: the roster, the bikes coming, the group chat. Coffee runs to weekend tours.
                </p>
                <Link
                  to="/hosting"
                  className={cta}
                  style={{ backgroundColor: dsColors.primaryStrong, color: '#ffffff', minWidth: '44px', minHeight: '44px' }}
                >
                  Create Your First Event
                  <ArrowIcon />
                </Link>
              </div>
              <div className="flex justify-center">
                <img src={shot('Event Details/iOS - Event Details - Participants')} alt="Event riders list in RidePulse showing the host and the motorcycles riders are bringing" loading="lazy" width={1320} height={2868} className={`${deviceFrame} w-52 sm:w-60 max-w-full`} style={{ borderColor: deviceBorder }} />
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* 5 · Manage — quiet ticks + GroupPulse teaser link */}
      <AnimatedSection delay={250}>
        <section className="py-16 md:py-24" style={{ backgroundColor: colors.surface }}>
          <div className="container mx-auto px-4 sm:px-6 max-w-3xl text-center">
            <p className="text-xs sm:text-sm font-bold uppercase tracking-widest mb-3" style={{ color: dsColors.treadGray }}>
              Manage Events
            </p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 leading-tight" style={{ color: heroPrimary, textWrap: 'balance' }}>
              You are in Control
            </h2>
            <p className="text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto mb-10" style={{ color: heroSecondary, textWrap: 'pretty' }}>
              Your event host dashboard puts everything at your fingertips. Manage participants, coordinate with your crew, and keep the group together with opt-in live location during the ride.
            </p>
            <div
              className="rounded-2xl p-6 sm:p-8 mb-10 text-left"
              style={{ backgroundColor: colors.background, border: `1px solid ${cardBorder}` }}
            >
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                {[
                  ['Participant Management', "Manage participation: who's joined, who's checked in.", null],
                  ['Event Chat', 'Coordinate and moderate conversations', null],
                  ['GroupPulse Tracking', "Stay up to date with the group's location", '/grouppulse'],
                  ['Event Data', 'Track attendance numbers and engagement', null]
                ].map(([title, sub, to]) => (
                  <li key={title as string} className="flex items-start gap-3">
                    <span className="mt-2 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: dsColors.primary }} aria-hidden="true" />
                    <div>
                      {to ? (
                        <Link to={to as string} className="font-semibold text-base mb-0.5 inline-flex items-center gap-1 hover:underline" style={{ color: heroPrimary }}>
                          {title} <ArrowIcon />
                        </Link>
                      ) : (
                        <h3 className="font-semibold text-base mb-0.5" style={{ color: heroPrimary }}>{title}</h3>
                      )}
                      <p className="text-sm" style={{ color: heroSecondary }}>{sub}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <Link
              to="/hosting"
              className={cta}
              style={{ color: heroPrimary, border: `2px solid ${dsColors.primary}`, minWidth: '44px', minHeight: '44px' }}
            >
              Managing Events
              <ArrowIcon />
            </Link>
          </div>
        </section>
      </AnimatedSection>

      {/* 6 · After the ride — copy left, evening-meetup panel right (subjects uncovered) */}
      <AnimatedSection delay={300}>
        <section className="py-16 md:py-24" style={{ backgroundColor: colors.background }}>
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-[5fr_6fr] gap-10 md:gap-14 items-center">
              <div className="text-center md:text-left">
                <p className="text-xs sm:text-sm font-bold uppercase tracking-widest mb-3" style={{ color: dsColors.warningStrong }}>
                  After the Ride
                </p>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 leading-tight" style={{ color: heroPrimary, textWrap: 'balance' }}>
                  Level Up Your Riding
                </h2>
                <p className="text-lg sm:text-xl leading-relaxed mb-8" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  Every ride counts toward your journey. Earn achievements, build your reputation, and connect with your riding community.
                </p>
                <Link
                  to="/social"
                  className={cta}
                  style={{ backgroundColor: dsColors.primaryStrong, color: '#ffffff', minWidth: '44px', minHeight: '44px' }}
                >
                  Explore Social Features
                  <ArrowIcon />
                </Link>
              </div>
              <DeferredBackground
                className="aspect-[4/3] rounded-2xl bg-cover bg-[position:30%_center] shadow-2xl"
                style={{ border: `1px solid ${cardBorder}` }}
                backgroundImage="url('/assets/images/marketing-concepts/ridepulse-community-evening-meetup-v1.webp')"
                role="img"
                aria-label="Riders talking beside their motorcycles outside a cafe at dusk"
              />
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* 7 · Garage — editorial image + copy split */}
      <AnimatedSection delay={350}>
        <section className="py-16 md:py-24" style={{ backgroundColor: colors.surface }}>
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center">
              <DeferredBackground
                className="aspect-[4/3] rounded-2xl bg-cover bg-center shadow-2xl order-2 md:order-1"
                style={{ border: `1px solid ${cardBorder}` }}
                backgroundImage="url('/assets/images/Personal Photos/2024-06-23_aprilia-rs660-garage-stripped-stand.webp')"
                role="img"
                aria-label="Motorcycle on a paddock stand inside a real garage"
              />
              <div className="text-center md:text-left order-1 md:order-2">
                <p className="text-xs sm:text-sm font-bold uppercase tracking-widest mb-3" style={{ color: dsColors.secondary }}>
                  Your Garage
                </p>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 leading-tight" style={{ color: heroPrimary, textWrap: 'balance' }}>
                  Bring Your Bike
                </h2>
                <p className="text-lg sm:text-xl leading-relaxed mb-8" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  Add the motorcycle you'll bring to an event, keep useful specs close at hand, and choose how your bikes appear on your public profile.
                </p>
                <Link
                  to="/garage"
                  className={cta}
                  style={{ color: heroPrimary, border: `2px solid ${dsColors.primary}`, minWidth: '44px', minHeight: '44px' }}
                >
                  Build Your Garage
                  <ArrowIcon />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* 8 · Close — night-road band, approved signup module */}
      <section className="relative bg-[#0a0d14] md:bg-transparent">
        <DeferredBackground className="md:hidden aspect-[16/10] bg-cover bg-[position:68%_center]" role="img" aria-label="Two riders on a desert highway at dusk" backgroundImage="url('/assets/images/marketing-concepts/ridepulse-night-road-two-riders-text-left-v1.webp')" />
        <DeferredBackground
          className="absolute inset-0 hidden md:block bg-cover bg-center"
          backgroundImage="linear-gradient(100deg, rgba(8,10,16,0.7) 0%, rgba(8,10,16,0.5) 45%, rgba(8,10,16,0.14) 72%, transparent 88%), url('/assets/images/marketing-concepts/ridepulse-night-road-two-riders-text-left-v1.webp')"
        />
        <div className="relative container mx-auto px-4 sm:px-6 max-w-6xl py-16 md:py-28">
          <div className="max-w-xl mx-auto md:mx-0 text-center md:text-left">
            <div className="mb-8 flex justify-center md:justify-start">
              <AppStoreBadges size="large" showApple showAndroid />
            </div>
          </div>
          <div className="max-w-xl mx-auto md:mx-0">
            <UpdatesSignup source="landing-page" variant="inline" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
